import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { customerApi } from '../utils/api';

export interface CustomerUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  slaTier: string;
  isImpersonated?: boolean;
}

interface CustomerAuthState {
  token: string | null;
  user: CustomerUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  mustChangePassword: boolean;

  // Actions
  login: (email: string, password: string, captchaToken?: string) => Promise<{
    success: boolean;
    mustChangePassword?: boolean;
    mfaRequired?: boolean;
    mfaSetupRequired?: boolean;
    mfaToken?: string;
  }>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: CustomerUser) => void;
  setMustChangePassword: (v: boolean) => void;
  setImpersonatedSession: (token: string, user: CustomerUser) => void;
  completeLogin: (user: CustomerUser, mustChangePassword?: boolean) => void;
  initialize: () => Promise<void>;
}

export const useCustomerAuthStore = create<CustomerAuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      mustChangePassword: false,

      login: async (email: string, password: string, captchaToken?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await customerApi.login({ email, password, captchaToken });
          const { user, mustChangePassword, mfaRequired, mfaSetupRequired, mfaToken } = response.data;

          if (mfaRequired || mfaSetupRequired) {
            set({
              token: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              mustChangePassword: false,
            });
            return { success: true, mfaRequired, mfaSetupRequired, mfaToken };
          }

          set({
            token: null,
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mustChangePassword: !!mustChangePassword,
            isInitialized: true,
          });
          return { success: true, mustChangePassword: !!mustChangePassword };
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Login fehlgeschlagen'
          });
          return { success: false };
        }
      },

      logout: async () => {
        try {
          await customerApi.logout();
        } catch {
          // Local logout still completes when the server is unreachable.
        }
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
          mustChangePassword: false,
          isInitialized: true,
        });
      },

      clearError: () => set({ error: null }),

      updateUser: (user: CustomerUser) => set({ user }),

      setMustChangePassword: (mustChangePassword: boolean) => set({ mustChangePassword }),

      setImpersonatedSession: (_token: string, user: CustomerUser) => {
        set({
          token: null,
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          mustChangePassword: false,
          isInitialized: true,
        });
      },

      completeLogin: (user: CustomerUser, mustChangePassword = false) => set({
        token: null,
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        mustChangePassword,
        isInitialized: true,
      }),
      initialize: async () => {
        try {
          const response = await customerApi.getSession();
          set({
            user: response.data.user,
            isAuthenticated: true,
            mustChangePassword: !!response.data.mustChangePassword,
            isInitialized: true,
            isLoading: false,
            error: null,
          });
        } catch {
          set({ user: null, isAuthenticated: false, mustChangePassword: false, isInitialized: true, isLoading: false });
        }
      },
    }),
    {
      name: 'customer-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword
      })
    }
  )
);
